import type { ComponentType, SVGProps } from 'react'
import clsx from 'clsx'
import { XClose } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { BUTTON_COLORS, BUTTON_SIZES, BUTTON_VARIANTS } from '@/components/button/constants'
import { IconButton } from '@/components/icon-button/IconButton'
import { ICON_BUTTON_COLORS, ICON_BUTTON_SIZES, ICON_BUTTON_VARIANTS } from '@/components/icon-button/constants'

export type ContextualAction = {
  /** Accessible name, and the visible label in wide containers. */
  label: string
  icon: ComponentType<SVGProps<SVGSVGElement>>
  /** Shown after the label only in the wide form; marks a jump out of Paxton. */
  trailingIcon?: ComponentType<SVGProps<SVGSVGElement>>
  /** Omit to render the action present but disabled, rather than removing it mid-selection. */
  onClick?: () => void
  loading?: boolean
  destructive?: boolean
}

interface ContextualActionBarProps {
  /** Rows selected; each folder counts as one item. */
  selectedCount: number
  actions: ContextualAction[]
  onClearSelection: () => void
}

/**
 * Shared selection bar for matter and synced-provider actions. The 56px row matches ActionBar.
 * Labels collapse below a 480px container width so the bar also fits beside the content viewer.
 * Both forms share accessible names; CSS exposes one form at each width.
 */
export function ContextualActionBar({ selectedCount, actions, onClearSelection }: ContextualActionBarProps) {
  const label = `${selectedCount.toLocaleString()} item${selectedCount === 1 ? '' : 's'} selected`

  return (
    // `py-3` around the 32px bar totals 56px. `ActionBar`, which this swaps places with, floors its
    // own row at the same 32px, so the two match as long as nothing in `ActionBar`'s content slot
    // grows past 32px — that floor is what the pairing rests on, and taller content would break it.
    <div className="px-4 py-3 [container-type:inline-size] md:px-0">
      {/* `rounded-full`, not `rounded-md`: the bar is a pill in the design (`--full, 9999px`); it is
          the buttons inside it that carry the 6px radius, which `Button` already applies. */}
      <div className="flex h-8 w-full items-center gap-4 rounded-full bg-brand-neutral-100 px-1">
        {/* Shrinkable, so the count truncates at narrow widths rather than pushing the actions off
            the bar. The actions stay whole: they are the part the bar exists for. */}
        <div className="flex min-w-0 items-center gap-1">
          <IconButton
            variant={ICON_BUTTON_VARIANTS.PLAIN}
            color={ICON_BUTTON_COLORS.BLACK}
            size={ICON_BUTTON_SIZES.SM}
            icon={<XClose />}
            aria-label="Clear selection"
            onClick={onClearSelection}
            wrapperClassName="shrink-0"
          />
          <span className="truncate text-sm font-medium tracking-[-0.15px] text-brand-neutral-950" title={label}>
            {label}
          </span>
        </div>

        {/* The compact variant spaces its icon buttons wider than the labelled row does. */}
        <div className="flex shrink-0 items-center gap-4 [@container_(min-width:_480px)]:gap-2.5">
          {actions.map(({ label: actionLabel, icon: Icon, trailingIcon: TrailingIcon, onClick, destructive, loading }, index) => (
            <div key={actionLabel} className="flex shrink-0 items-center gap-4 [@container_(min-width:_480px)]:gap-2.5">
              <BarDivider className={index > 0 ? 'hidden [@container_(min-width:_480px)]:block' : undefined} />
              <Button
                onClick={onClick}
                disabled={!onClick}
                loading={loading}
                aria-busy={loading}
                variant={BUTTON_VARIANTS.PLAIN}
                color={destructive ? BUTTON_COLORS.DESTRUCTIVE : BUTTON_COLORS.SECONDARY}
                size={BUTTON_SIZES.SM}
                leadingIcon={<Icon className="h-4 w-4" />}
                trailingIcon={TrailingIcon ? <TrailingIcon className="h-4 w-4" /> : undefined}
                className="@max-[480px]:hidden [@container_(min-width:_480px)]:inline-flex"
              >
                {actionLabel}
              </Button>
              <IconButton
                variant={ICON_BUTTON_VARIANTS.PLAIN}
                color={destructive ? ICON_BUTTON_COLORS.DESTRUCTIVE : ICON_BUTTON_COLORS.BLACK}
                size={ICON_BUTTON_SIZES.MD}
                icon={<Icon />}
                aria-label={actionLabel}
                disabled={!onClick}
                loading={loading}
                aria-busy={loading}
                onClick={onClick}
                wrapperClassName="[@container_(min-width:_480px)]:hidden"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Rule separating the actions from the count. Shorter in the compact variant, where it is the only
 * divider on the bar; full-height beside the labelled buttons.
 */
function BarDivider({ className }: { className?: string }) {
  return <div className={clsx('h-5 w-px shrink-0 bg-brand-neutral-300 [@container_(min-width:_480px)]:h-8', className)} aria-hidden="true" />
}
